import { supabase } from '../lib/supabase';

export type InquiryType = 'general_inquiry' | 'general_support' | 'sales_questions' | 'work_with_us';

export interface ContactSubmission {
  id: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  email: string;
  inquiry_type: InquiryType;
  message: string;
  is_read: boolean;
  created_at: string;
  updated_at: string;
  attachment_count?: number;
}

export interface ContactAttachment {
  id: string;
  submission_id: string;
  file_name: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  created_at: string;
}

export interface ContactFormData {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  inquiryType: InquiryType;
  message: string;
  files: File[];
}

class ContactService {
  async submitContactForm(formData: ContactFormData): Promise<{ success: boolean; error?: string }> {
    try {
      const { data: submission, error: submissionError } = await supabase
        .from('contact_submissions')
        .insert({
          first_name: formData.firstName,
          last_name: formData.lastName,
          phone_number: formData.phoneNumber,
          email: formData.email,
          inquiry_type: formData.inquiryType,
          message: formData.message,
        })
        .select()
        .single();

      if (submissionError || !submission) {
        console.error('Error creating submission:', submissionError);
        return { success: false, error: 'Failed to submit form. Please try again.' };
      }

      if (formData.files.length > 0) {
        const uploadResult = await this.uploadAttachments(submission.id, formData.files);
        if (!uploadResult.success) {
          console.error('Error uploading attachments:', uploadResult.error);
        }
      }

      return { success: true };
    } catch (error) {
      console.error('Error submitting contact form:', error);
      return { success: false, error: 'An unexpected error occurred. Please try again.' };
    }
  }

  async uploadAttachments(submissionId: string, files: File[]): Promise<{ success: boolean; error?: string }> {
    try {
      const uploadedFiles: ContactAttachment[] = [];

      for (const file of files) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${submissionId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('contact-attachments')
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: false,
          });

        if (uploadError) {
          console.error('Error uploading file:', uploadError);
          continue;
        }

        const { error: attachmentError } = await supabase
          .from('contact_attachments')
          .insert({
            submission_id: submissionId,
            file_name: file.name,
            file_path: uploadData.path,
            file_size: file.size,
            mime_type: file.type,
          });

        if (attachmentError) {
          console.error('Error saving attachment record:', attachmentError);
        }
      }

      return { success: true };
    } catch (error) {
      console.error('Error in uploadAttachments:', error);
      return { success: false, error: 'Failed to upload some attachments' };
    }
  }

  async getSubmissions(): Promise<ContactSubmission[]> {
    try {
      const { data, error } = await supabase.rpc('get_contact_submissions_with_attachments');

      if (error) {
        console.error('Error fetching submissions:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getSubmissions:', error);
      return [];
    }
  }

  async getAttachments(submissionId: string): Promise<ContactAttachment[]> {
    try {
      const { data, error } = await supabase
        .from('contact_attachments')
        .select('*')
        .eq('submission_id', submissionId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching attachments:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getAttachments:', error);
      return [];
    }
  }

  async getAttachmentUrl(filePath: string): Promise<string | null> {
    try {
      const { data, error } = await supabase.storage
        .from('contact-attachments')
        .createSignedUrl(filePath, 3600);

      if (error) {
        console.error('Error creating signed URL:', error);
        return null;
      }

      return data.signedUrl;
    } catch (error) {
      console.error('Error getting attachment URL:', error);
      return null;
    }
  }

  async markAsRead(submissionId: string, isRead: boolean): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('contact_submissions')
        .update({ is_read: isRead })
        .eq('id', submissionId);

      if (error) {
        console.error('Error updating submission:', error);
        return { success: false, error: 'Failed to update submission' };
      }

      return { success: true };
    } catch (error) {
      console.error('Error in markAsRead:', error);
      return { success: false, error: 'An unexpected error occurred' };
    }
  }

  async deleteSubmission(submissionId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const attachments = await this.getAttachments(submissionId);

      for (const attachment of attachments) {
        await supabase.storage
          .from('contact-attachments')
          .remove([attachment.file_path]);
      }

      const { error } = await supabase
        .from('contact_submissions')
        .delete()
        .eq('id', submissionId);

      if (error) {
        console.error('Error deleting submission:', error);
        return { success: false, error: 'Failed to delete submission' };
      }

      return { success: true };
    } catch (error) {
      console.error('Error in deleteSubmission:', error);
      return { success: false, error: 'An unexpected error occurred' };
    }
  }
}

export const contactService = new ContactService();
